import { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { Loader2, AlertCircle, Printer } from "lucide-react";
import { useTranslation } from "react-i18next";
import api from "@/lib/api";

// ─────────────────────────────────────────────────────────────────────────────
// TRADE JOURNAL PDF EXPORT — printable route.
//
// Renders a full weekly review — the student's own trade notes AND the
// Professor's recap — in a print-optimized A4 layout with dedicated
// @media print styles. Opens with `?auto=1` to auto-trigger window.print()
// so the "Export PDF" button in TradeJournal.jsx becomes a one-click PDF.
//
// Data source: GET /api/journal/weekly-reports/{id}/print-data
// ─────────────────────────────────────────────────────────────────────────────

const PRINT_CSS = `
@page { size: A4; margin: 18mm 16mm 20mm 16mm; }
@media print {
  .no-print { display: none !important; }
  body { background: white !important; }
  .print-page { box-shadow: none !important; margin: 0 !important; page-break-after: always; }
  .print-page:last-child { page-break-after: auto; }
  .avoid-break { break-inside: avoid; }
}
@media screen {
  body { background: #f1f5f9; }
}
`;

function fmtDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" });
  } catch {
    return iso;
  }
}

function fmtTime(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString("en-US", { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch {
    return iso;
  }
}

/** Very small markdown → HTML renderer for the Professor's recap.
 * The Professor is instructed to output markdown-ish text: headings,
 * bullet points, bold. We render a safe subset — no arbitrary HTML. */
function renderProfessorMarkdown(md) {
  if (!md) return null;
  const lines = md.split("\n");
  const out = [];
  let buffer = [];
  const flushPara = () => {
    if (!buffer.length) return;
    const text = buffer.join(" ").trim();
    if (text) {
      out.push(
        <p key={out.length} className="text-slate-700 leading-relaxed my-3">
          {applyInline(text)}
        </p>,
      );
    }
    buffer = [];
  };
  const applyInline = (text) => {
    // **bold** and *italic*
    const parts = [];
    let rest = text;
    let key = 0;
    const bold = /\*\*(.+?)\*\*/;
    while (bold.test(rest)) {
      const m = rest.match(bold);
      parts.push(rest.slice(0, m.index));
      parts.push(
        <strong key={key++} className="font-semibold text-slate-900">
          {m[1]}
        </strong>,
      );
      rest = rest.slice(m.index + m[0].length);
    }
    parts.push(rest);
    return parts;
  };

  lines.forEach((raw) => {
    const line = raw.trimEnd();
    if (!line.trim()) {
      flushPara();
      return;
    }
    if (line.startsWith("### ")) {
      flushPara();
      out.push(
        <h4 key={out.length} className="font-display font-bold text-base text-slate-900 mt-5 mb-2 uppercase tracking-wide">
          {line.replace(/^###\s+/, "")}
        </h4>,
      );
      return;
    }
    if (line.startsWith("## ")) {
      flushPara();
      out.push(
        <h3 key={out.length} className="font-display font-bold text-lg text-slate-900 mt-6 mb-2">
          {line.replace(/^##\s+/, "")}
        </h3>,
      );
      return;
    }
    if (line.startsWith("# ")) {
      flushPara();
      out.push(
        <h2 key={out.length} className="font-display font-black text-2xl text-slate-900 mt-6 mb-3">
          {line.replace(/^#\s+/, "")}
        </h2>,
      );
      return;
    }
    if (/^\s*[-*]\s+/.test(line)) {
      flushPara();
      out.push(
        <ul key={out.length} className="list-disc ml-6 my-2 text-slate-700 leading-relaxed">
          <li>{applyInline(line.replace(/^\s*[-*]\s+/, ""))}</li>
        </ul>,
      );
      return;
    }
    buffer.push(line);
  });
  flushPara();
  return out;
}

function TradeCard({ entry, idx, t }) {
  const pnl = entry.pnl;
  const green = typeof pnl === "number" && pnl >= 0;
  return (
    <div className="avoid-break border border-slate-300 rounded-lg p-4 mb-3 bg-white">
      <div className="flex items-start justify-between gap-4 mb-2">
        <div>
          <div className="text-[10px] uppercase tracking-[0.24em] text-slate-500 font-semibold">
            {t("journalExportUi.tradeNumber", { number: idx + 1 })} · {fmtTime(entry.created_at)}
          </div>
          <div className="font-display font-bold text-lg text-slate-900 mt-0.5">
            {entry.instrument} · <span className="uppercase">{entry.direction}</span>
          </div>
        </div>
        {typeof pnl === "number" && (
          <div
            className={`text-right font-display font-bold text-lg ${
              green ? "text-emerald-700" : "text-red-700"
            }`}
          >
            {green ? "+" : ""}${pnl.toFixed(2)}
            {entry.r_multiple != null && (
              <div className="text-[11px] font-semibold uppercase tracking-widest text-slate-500 mt-0.5">
                {entry.r_multiple > 0 ? "+" : ""}
                {entry.r_multiple.toFixed(2)}R
              </div>
            )}
          </div>
        )}
      </div>

      <div className="grid grid-cols-4 gap-3 text-xs mt-3 border-t border-slate-100 pt-3">
        <div>
          <div className="text-slate-500 uppercase tracking-widest text-[10px] font-semibold">{t("journalExportUi.entry")}</div>
          <div className="font-semibold text-slate-900 mt-0.5">{entry.entry ?? "—"}</div>
        </div>
        <div>
          <div className="text-slate-500 uppercase tracking-widest text-[10px] font-semibold">{t("journalExportUi.stop")}</div>
          <div className="font-semibold text-slate-900 mt-0.5">{entry.stop ?? "—"}</div>
        </div>
        <div>
          <div className="text-slate-500 uppercase tracking-widest text-[10px] font-semibold">{t("journalExportUi.exit")}</div>
          <div className="font-semibold text-slate-900 mt-0.5">{entry.exit ?? "—"}</div>
        </div>
        <div>
          <div className="text-slate-500 uppercase tracking-widest text-[10px] font-semibold">{t("journalExportUi.size")}</div>
          <div className="font-semibold text-slate-900 mt-0.5">{entry.size ?? "—"}</div>
        </div>
      </div>

      {entry.notes && (
        <div className="mt-3 border-t border-slate-100 pt-3">
          <div className="text-[10px] uppercase tracking-[0.24em] text-blue-800 font-semibold mb-1.5">
            {t("journalExportUi.yourNotes")}
          </div>
          <p className="text-sm text-slate-700 leading-relaxed whitespace-pre-wrap">
            {entry.notes}
          </p>
        </div>
      )}
    </div>
  );
}

export default function JournalExport() {
  const { t } = useTranslation();
  const { reportId } = useParams();
  const [searchParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    api.get(`/journal/weekly-reports/${reportId}/print-data`)
      .then((r) => setData(r.data))
      .catch((e) => setError(e.response?.data?.detail || e.message || t("journalExportUi.errorFailedToLoad")));
  }, [reportId]);

  useEffect(() => {
    // Auto-print once the data has rendered — only if ?auto=1 was passed
    // (i.e. we came from the "Export PDF" button, not a manual URL visit).
    if (data && searchParams.get("auto") === "1") {
      const t = setTimeout(() => window.print(), 600);
      return () => clearTimeout(t);
    }
  }, [data, searchParams]);

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-100 p-8">
        <div className="max-w-md text-center bg-white rounded-2xl border border-red-200 p-8">
          <AlertCircle className="mx-auto text-red-600" size={32} />
          <div className="mt-4 font-display font-bold text-lg text-slate-900">
            {t("journalExportUi.errorTitle")}
          </div>
          <div className="mt-2 text-sm text-slate-600">{error}</div>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-100">
        <div className="flex items-center gap-3 text-slate-600">
          <Loader2 size={20} className="animate-spin" /> {t("journalExportUi.loading")}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-8 sm:py-12">
      <style>{PRINT_CSS}</style>

      {/* Top toolbar — hidden on print */}
      <div className="no-print max-w-[210mm] mx-auto mb-6 px-6 flex items-center justify-between gap-4">
        <div className="text-sm text-slate-600">
          {t("journalExportUi.printInstructions")}
        </div>
        <button
          type="button"
          onClick={() => window.print()}
          className="inline-flex items-center gap-2 bg-blue-800 hover:bg-blue-700 text-white text-sm font-semibold px-5 py-2.5 rounded-md"
        >
          <Printer size={16} /> {t("journalExportUi.saveAsPdf")}
        </button>
      </div>

      {/* Printable page */}
      <article className="print-page max-w-[210mm] mx-auto bg-white shadow-xl rounded-lg p-10 sm:p-14">
        {/* Letterhead */}
        <header className="flex items-start justify-between gap-6 pb-6 border-b border-slate-200">
          <div>
            <div className="text-[10px] uppercase tracking-[0.32em] text-amber-600 font-bold">
              Peak Market Society
            </div>
            <h1 className="font-display font-black text-3xl text-slate-900 tracking-tight mt-2">
              {t("journalExportUi.title")}
            </h1>
            {data.student_name && (
              <div className="mt-2 text-sm text-slate-600">
                {t("journalExportUi.preparedFor", { name: data.student_name })}
              </div>
            )}
          </div>
          <div className="text-right text-xs text-slate-500 leading-relaxed">
            <div className="font-semibold text-slate-900 uppercase tracking-widest text-[10px]">
              {t("journalExportUi.period")}
            </div>
            <div className="mt-1">
              {fmtDate(data.period_start)} → {fmtDate(data.period_end)}
            </div>
            <div className="mt-3 font-semibold text-slate-900 uppercase tracking-widest text-[10px]">
              {t("journalExportUi.trades")}
            </div>
            <div className="mt-1">{data.trade_count}</div>
            <div className="mt-3 font-semibold text-slate-900 uppercase tracking-widest text-[10px]">
              {t("journalExportUi.generated")}
            </div>
            <div className="mt-1">{fmtDate(data.generated_at)}</div>
          </div>
        </header>

        {/* Section 1 — Professor's recap */}
        <section className="mt-8">
          <div className="text-[10px] uppercase tracking-[0.28em] text-blue-800 font-bold">
            {t("journalExportUi.professorReviewLabel")}
          </div>
          <h2 className="font-display font-black text-2xl text-slate-900 tracking-tight mt-2 mb-4">
            {t("journalExportUi.professorReviewTitle")}
          </h2>
          <div className="text-slate-700 leading-relaxed">
            {data.content
              ? renderProfessorMarkdown(data.content)
              : <p className="italic text-slate-500">{t("journalExportUi.noRecap")}</p>}
          </div>
        </section>

        {/* Section 2 — Your trades with your notes */}
        <section className="mt-10">
          <div className="text-[10px] uppercase tracking-[0.28em] text-amber-600 font-bold">
            {t("journalExportUi.yourTradesLabel")}
          </div>
          <h2 className="font-display font-black text-2xl text-slate-900 tracking-tight mt-2 mb-4">
            {t("journalExportUi.yourTradesTitle")}
          </h2>

          {data.entries && data.entries.length > 0 ? (
            data.entries.map((e, i) => <TradeCard key={e.id} entry={e} idx={i} t={t} />)
          ) : (
            <p className="italic text-slate-500">{t("journalExportUi.noEntries")}</p>
          )}
        </section>

        {/* Footer */}
        <footer className="mt-12 pt-6 border-t border-slate-200 text-[10px] text-slate-500 uppercase tracking-widest text-center">
          {t("journalExportUi.footer")}
        </footer>
      </article>
    </div>
  );
}
